import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { PageHeader } from '../../components/ui/PageHeader';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { apiClient } from '../../lib/api-client';
import { useAuth } from '../auth/auth-context';

import { SEV_LABEL } from './IncidentsListPage';

type Severity = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
type Status = 'OPEN' | 'IN_PROGRESS' | 'RESOLVED';

interface IncidentDetail {
  id: string;
  title: string;
  severity: Severity;
  status: Status;
  createdAt: string;
  resolvedAt: string | null;
  internalComment: string | null;
  automation: { id: string; name: string };
  execution: { id: string; externalExecutionId: string; status: string } | null;
}

async function fetchIncident(id: string): Promise<IncidentDetail> {
  const res = await apiClient.get<IncidentDetail>(`/incidents/${id}`);
  return res.data;
}

export function IncidentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const qc = useQueryClient();
  const isPlatform = user?.role === 'SUPER_ADMIN' || user?.role === 'ADWEB_OPERATOR';

  const query = useQuery({
    queryKey: ['incident', id],
    queryFn: () => fetchIncident(id!),
    enabled: !!id,
  });

  const [status, setStatus] = useState<Status>('OPEN');
  const [severity, setSeverity] = useState<Severity>('MEDIUM');
  const [comment, setComment] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (query.data) {
      setStatus(query.data.status);
      setSeverity(query.data.severity);
      setComment(query.data.internalComment ?? '');
    }
  }, [query.data]);

  const update = useMutation({
    mutationFn: async (body: Record<string, unknown>) => apiClient.patch(`/incidents/${id}`, body),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['incident', id] });
      await qc.invalidateQueries({ queryKey: ['incidents'] });
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2500);
    },
  });

  if (query.isLoading) {
    return <div className="p-8 text-sm text-slate-500">Chargement…</div>;
  }
  if (!query.data) {
    return <div className="p-8 text-sm text-slate-500">Introuvable.</div>;
  }
  const incident = query.data;

  return (
    <>
      <PageHeader
        title={incident.title}
        subtitle={incident.automation.name}
        actions={<StatusBadge status={incident.status} />}
      />
      <section className="grid grid-cols-1 gap-6 px-4 sm:px-6 lg:px-8 py-6 lg:grid-cols-2">
        <div className="surface p-5">
          <h2 className="mb-4 text-sm font-medium text-slate-700">Informations</h2>
          <dl className="space-y-3 text-sm">
            <div>
              <dt className="text-xs uppercase tracking-wider text-slate-500">Sévérité</dt>
              <dd className="mt-0.5 text-slate-900">{SEV_LABEL[incident.severity]}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wider text-slate-500">Ouvert le</dt>
              <dd className="mt-0.5 text-slate-900">
                {new Date(incident.createdAt).toLocaleString('fr-FR')}
              </dd>
            </div>
            {incident.resolvedAt && (
              <div>
                <dt className="text-xs uppercase tracking-wider text-slate-500">Résolu le</dt>
                <dd className="mt-0.5 text-slate-900">
                  {new Date(incident.resolvedAt).toLocaleString('fr-FR')}
                </dd>
              </div>
            )}
            {incident.execution && (
              <div>
                <dt className="text-xs uppercase tracking-wider text-slate-500">Exécution liée</dt>
                <dd className="mt-0.5 text-slate-900">{incident.execution.externalExecutionId}</dd>
              </div>
            )}
          </dl>
        </div>

        {/* Gestion réservée aux rôles Adweb-Systèmes */}
        {isPlatform ? (
          <div className="surface p-5">
            <h2 className="mb-4 text-sm font-medium text-slate-700">Gestion (Adweb-Systèmes)</h2>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label
                    htmlFor="inc-status"
                    className="mb-1 block text-xs font-medium text-slate-600"
                  >
                    Statut
                  </label>
                  <select
                    id="inc-status"
                    value={status}
                    onChange={(e) => setStatus(e.target.value as Status)}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  >
                    <option value="OPEN">Ouvert</option>
                    <option value="IN_PROGRESS">En cours</option>
                    <option value="RESOLVED">Résolu</option>
                  </select>
                </div>
                <div>
                  <label
                    htmlFor="inc-severity"
                    className="mb-1 block text-xs font-medium text-slate-600"
                  >
                    Sévérité
                  </label>
                  <select
                    id="inc-severity"
                    value={severity}
                    onChange={(e) => setSeverity(e.target.value as Severity)}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  >
                    <option value="LOW">Basse</option>
                    <option value="MEDIUM">Moyenne</option>
                    <option value="HIGH">Haute</option>
                    <option value="CRITICAL">Critique</option>
                  </select>
                </div>
              </div>
              <div>
                <label
                  htmlFor="inc-comment"
                  className="mb-1 block text-xs font-medium text-slate-600"
                >
                  Note interne (invisible côté client)
                </label>
                <textarea
                  id="inc-comment"
                  rows={3}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  placeholder="Diagnostic, actions menées…"
                />
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() =>
                    update.mutate({ status, severity, internalComment: comment || null })
                  }
                  disabled={update.isPending}
                  className="rounded-lg bg-brand px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-700 disabled:opacity-50"
                >
                  {update.isPending ? 'Enregistrement…' : 'Enregistrer'}
                </button>
                {incident.status !== 'RESOLVED' && (
                  <button
                    type="button"
                    onClick={() => update.mutate({ resolved: true })}
                    disabled={update.isPending}
                    className="rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
                  >
                    Marquer résolu
                  </button>
                )}
                {saved && <span className="text-xs text-emerald-600">Enregistré ✓</span>}
              </div>
            </div>
          </div>
        ) : (
          incident.internalComment && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-5">
              <h2 className="mb-2 text-sm font-medium text-amber-900">Note interne</h2>
              <p className="whitespace-pre-line text-sm text-amber-900">
                {incident.internalComment}
              </p>
            </div>
          )
        )}
      </section>
    </>
  );
}
